import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import {
  useNavigate,
  useParams,
  useRouter,
  useSearch
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { formSchema } from '@/pages/artist/onboard/components/stages/languages/schema';
import {
  useArtistProfileLanguagesMutation,
  useArtistProfileLanguagesQuery
} from '@/shared/api/requests';

import type { ArtistLanguageDto } from '../../../../../../../../@types/types';
import { ArtistLanguageLevels } from '../../../../../../../../@types/types';

export const useArtistLanguages = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate({ from: '/artist/$id/onboard/$step' });
  const { id: profileId } = useParams({ from: '/artist/$id' });
  const languages = useArtistProfileLanguagesQuery({ profileId });
  const search = useSearch({ from: '/artist/$id/_onboard/onboard/$step' });
  const router = useRouter();
  const queryClient = useQueryClient();

  const next = () => {
    if (search.from !== undefined) {
      router.history.back();
    }
    navigate({
      to: '/artist/$id/onboard/$step',
      params: { step: 'parameters' }
    });
  };
  const languageMutation = useArtistProfileLanguagesMutation({
    options: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['ArtistProfileLanguages', profileId]
        });
        next();
      },
      onError: () => {
        notifications.show({
          title: formatMessage({ id: 'notifications.errorOnDataUpdate' }),
          ...notificationStyleConfig
        });
      }
    }
  });
  const deleteLanguage = useArtistProfileLanguagesMutation();
  const form = useForm<{
    native: ArtistLanguageDto;
    languages: ArtistLanguageDto[];
  }>({
    initialValues: {
      native: {
        name: '',
        level: ArtistLanguageLevels.Native
      },
      languages: []
    },
    validate: zodResolver(formSchema)
  });

  const onRemoveItem = async (index: number) => {
    form.removeListItem('languages', index);
    const data = () => {
      if (form.getValues().native.name) {
        return [form.getValues().native, ...form.getValues().languages];
      }
      return [...form.getValues().languages];
    };
    await deleteLanguage.mutateAsync({
      params: {
        profileId,
        data: data()
      }
    });
  };

  useEffect(() => {
    if (languages.isSuccess) {
      const native = languages.data.data.filter(
        (item) => item.level === ArtistLanguageLevels.Native
      );
      const lang = languages.data.data.filter(
        (item) => item.level !== ArtistLanguageLevels.Native
      );

      if (native[0]) {
        form.setFieldValue('native', native[0]);
      }
      if (lang) {
        form.setFieldValue('languages', lang);
      }
    }
  }, [languages.data?.data]);

  const onSubmit = form.onSubmit(async (values) => {
    const data = () => {
      if (values.native.name) {
        return [values.native, ...values.languages];
      }
      return [...values.languages];
    };
    await languageMutation.mutateAsync({
      params: {
        profileId,
        data: data()
      }
    });
  });

  const isValid = !!form.getValues().native.name;

  const queries = [languages];

  const isLoading = queries.some((q) => q.isLoading);

  return {
    state: { isLoading, form, isValid },
    functions: { onSubmit, onRemoveItem }
  };
};
